import { useEffect, useState } from 'react';
import { Shield, UserPlus, Trash2, Crown } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider.js';
import { addUser, removeUser, setAdmin, listUsers, type AllowedUser } from '../lib/callables.js';

const MASTER_ADMIN = 'jordan@recruitwithatlas.com';

export function TeamPage() {
  const { userRecord } = useAuth();
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const result = await listUsers();
      setUsers(result.data.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  if (!userRecord?.isAdmin) {
    return (
      <div className="p-8 text-sm text-[#73726e]">You don't have admin access.</div>
    );
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setActionError(null);
    setAdding(true);
    try {
      await addUser({ email: newEmail.trim().toLowerCase(), isAdmin: newIsAdmin });
      setNewEmail('');
      setNewIsAdmin(false);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add user.');
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(email: string) {
    if (!confirm(`Remove ${email}?`)) return;
    setActionError(null);
    try {
      await removeUser({ email });
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove user.');
    }
  }

  async function handleToggleAdmin(email: string, current: boolean) {
    setActionError(null);
    try {
      await setAdmin({ email, isAdmin: !current });
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update role.');
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-8 py-8">
      <div className="flex items-center gap-2 mb-6">
        <Shield size={16} className="text-[#73726e]" />
        <h1 className="text-lg font-semibold text-[#37352f]">Team</h1>
      </div>

      {/* Add user form */}
      <form onSubmit={handleAdd} className="flex gap-2 mb-6 items-end">
        <label className="flex-1 flex flex-col gap-1">
          <span className="text-xs font-medium text-[#73726e]">Add user by email</span>
          <input
            required
            type="email"
            placeholder="colleague@recruitwithatlas.com"
            className="px-2.5 py-1.5 text-sm border border-[#e9e9e7] rounded focus:outline-none focus:ring-1 focus:ring-[#2383e2] text-[#37352f] placeholder:text-[#aeaaa4]"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-1.5 pb-1.5 text-sm text-[#73726e] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={newIsAdmin}
            onChange={(e) => setNewIsAdmin(e.target.checked)}
            className="rounded"
          />
          Admin
        </label>
        <button
          type="submit"
          disabled={adding}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#37352f] rounded hover:bg-[#2e2e2c] disabled:opacity-50 transition-colors"
        >
          <UserPlus size={13} />
          {adding ? 'Adding…' : 'Add'}
        </button>
      </form>

      {(error || actionError) && (
        <p className="text-xs text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 mb-4">
          {error ?? actionError}
        </p>
      )}

      {/* User list */}
      {loading ? (
        <div className="text-sm text-[#aeaaa4]">Loading…</div>
      ) : (
        <div className="border border-[#e9e9e7] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e9e9e7] bg-[#f7f7f5]">
                {['User', 'Role', ''].map((h) => (
                  <th key={h} className="py-2 px-4 text-left text-xs font-medium text-[#aeaaa4]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMaster = u.email === MASTER_ADMIN;
                return (
                  <tr key={u.id} className="border-b border-[#f1f1ef] last:border-0 hover:bg-[#fafafa]">
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        {isMaster && <Crown size={12} className="text-[#d9730d] shrink-0" />}
                        <div>
                          <p className="text-[#37352f] font-medium text-xs">
                            {u.displayName ?? u.email}
                          </p>
                          {u.displayName && (
                            <p className="text-[10px] text-[#aeaaa4]">{u.email}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                        u.isAdmin
                          ? 'bg-[#edf4fd] text-[#2383e2]'
                          : 'bg-[#f1f1ef] text-[#73726e]'
                      }`}>
                        {u.isAdmin ? 'Admin' : 'Member'}
                      </span>
                    </td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2 justify-end">
                        {!isMaster && (
                          <>
                            <button
                              onClick={() => handleToggleAdmin(u.email, u.isAdmin)}
                              className="text-xs text-[#73726e] hover:text-[#2383e2] transition-colors"
                              title={u.isAdmin ? 'Demote to member' : 'Promote to admin'}
                            >
                              {u.isAdmin ? 'Demote to member' : 'Promote to admin'}
                            </button>
                            <button
                              onClick={() => handleRemove(u.email)}
                              className="text-[#aeaaa4] hover:text-[#e03e3e] transition-colors"
                              title="Remove user"
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-sm text-[#aeaaa4]">No users yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
